import type { ReactNode } from "react";
import { groupTimelineByDay, type TimelineDayGroup } from "@/utils/timeline";

/**
 * Props for the shared {@link TimelineShell} component.
 *
 * The shell is deliberately agnostic about the data it renders: pages pass
 * entries plus resolvers for the anchor timestamp (day separators) and the
 * unique key, and a render function for the page-specific entry card.
 */
export interface TimelineShellProps<TItem> {
  /** Entries in display order (newest first); day separators are derived from `getTimestamp`. */
  entries: TItem[];
  /** Resolves the anchor timestamp used to group entries by day. */
  getTimestamp: (entry: TItem) => string;
  /** Resolves a stable, unique key for each entry (used as the React key). */
  getEntryKey: (entry: TItem) => string | number;
  /** Renders the page-specific card for a single entry. */
  renderEntry: (entry: TItem) => ReactNode;
  /** True while the first page of data is still loading and there are no entries yet. */
  loading: boolean;
  /** Error message; when set, an error banner with a retry button is shown above the content. */
  error: string | null;
  /** Retry handler shown in the error banner; `null` hides the button. */
  onRetry: (() => void) | null;
  /** True when the report scope is not chosen yet; renders the `notReadyMessage` hint instead. */
  notReady?: boolean;
  /** Hint text shown when `notReady` is true. Defaults to a generic scope hint. */
  notReadyMessage?: string;
  /** Message shown when there are no entries and no error. Defaults to a generic empty state. */
  emptyMessage?: string;
}

const DEFAULT_NOT_READY_MESSAGE = "Ajuste o escopo do relatório para carregar a linha do tempo de eventos.";
const DEFAULT_EMPTY_MESSAGE = "Nenhum evento encontrado para o intervalo selecionado.";

/** Renders the loading skeleton cards shown while the first page of data loads. */
function TimelineSkeletons() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 3 }).map((_, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: fixed, non-reordering placeholder skeletons
        <div key={index} className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="h-4 w-40 animate-pulse rounded bg-gray-200" />
          <div className="mt-3 h-3 w-56 animate-pulse rounded bg-gray-100" />
          <div className="mt-4 space-y-2">
            <div className="h-3 w-full animate-pulse rounded bg-gray-100" />
            <div className="h-3 w-2/3 animate-pulse rounded bg-gray-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Renders the error banner with an optional retry button.
 *
 * @param message - Error message to display.
 * @param onRetry - Retry handler; `null` hides the button.
 */
function TimelineErrorBanner({ message, onRetry }: { message: string; onRetry: (() => void) | null }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
      <span className="font-medium">{message}</span>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center rounded-md border border-red-300 bg-white px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-100"
        >
          Tentar novamente
        </button>
      ) : null}
    </div>
  );
}

/** Renders the empty state when no entries exist for the selected range. */
function TimelineEmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 text-center text-sm text-gray-600">{message}</div>
  );
}

/**
 * Renders the day-grouped timeline: the vertical line with day separator
 * headers and the page-specific entry cards.
 */
function TimelineList<TItem>({
  groups,
  getEntryKey,
  renderEntry,
}: {
  groups: TimelineDayGroup<TItem>[];
  getEntryKey: (entry: TItem) => string | number;
  renderEntry: (entry: TItem) => ReactNode;
}) {
  return (
    <div className="relative">
      <div className="absolute left-3 top-0 h-full w-px bg-gray-200" aria-hidden />
      <div className="space-y-6">
        {groups.map((group) => (
          <div key={group.dayKey} className="space-y-6">
            <div className="relative">
              <span
                className="absolute left-3 top-1.5 inline-flex h-3 w-3 -translate-x-1/2 rounded-full border border-gray-400 bg-white"
                aria-hidden
              />
              <span className="ml-10 inline-block rounded-md bg-gray-200/70 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-gray-700">
                {group.label}
              </span>
            </div>
            {group.entries.map((entry) => (
              <div key={getEntryKey(entry)}>{renderEntry(entry)}</div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Shared timeline shell: vertical line + dots, day separators, loading
 * skeletons, empty state, and an error banner with retry. It is fully
 * props-driven so any report page can use it; entry cards stay page-specific.
 *
 * @param props - See {@link TimelineShellProps}.
 */
export function TimelineShell<TItem>({
  entries,
  getTimestamp,
  getEntryKey,
  renderEntry,
  loading,
  error,
  onRetry,
  notReady = false,
  notReadyMessage,
  emptyMessage,
}: TimelineShellProps<TItem>) {
  if (notReady) {
    return (
      <div className="rounded-xl border border-dashed border-neutral-300 bg-neutral-50 p-6 text-center text-sm text-neutral-600">
        {notReadyMessage ?? DEFAULT_NOT_READY_MESSAGE}
      </div>
    );
  }

  const groups = groupTimelineByDay(entries, getTimestamp);

  return (
    <div className="space-y-4">
      {error ? <TimelineErrorBanner message={error} onRetry={onRetry} /> : null}
      {loading && entries.length === 0 ? (
        <TimelineSkeletons />
      ) : entries.length === 0 ? (
        <TimelineEmptyState message={emptyMessage ?? DEFAULT_EMPTY_MESSAGE} />
      ) : (
        <TimelineList groups={groups} getEntryKey={getEntryKey} renderEntry={renderEntry} />
      )}
    </div>
  );
}
